import { GiftIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { isIosNative } from '@/renderer/utils/isIosNative';

// The sportsbook offers live on our content site (sportsbookbonuses.com) rather
// than linking straight to the books — required for X-ads compliance (#1239) and
// the strategic funnel (#1268). External, so open in a new tab / the system
// browser on mobile instead of navigating away from the app.
export const PROMOS: { href: string; text: string }[] = [
  {
    href: 'https://sportsbookbonuses.com/betonline',
    text: 'Get $250 credit with BetOnline',
  },
  {
    href: 'https://sportsbookbonuses.com/bet105',
    text: 'Get $250 credit with bet105',
  },
  {
    href: 'https://sportsbookbonuses.com/oddslogic',
    text: 'Get $250 credit with OddsLogic',
  },
];

const ROTATE_MS = 6000;

/**
 * Rotating sportsbook promo: the desktop sidebar footer and the phone /more
 * page (#2142).
 *
 * NEVER rendered inside the iOS native app (App Review gambling sensitivity).
 * The gate lives here, not in the callers, and it fails closed: nothing renders
 * on the server or on the first client render, and the promo appears only once
 * an effect has confirmed this is not the iOS shell. So an iOS user never sees
 * even a one-frame flash, whichever page mounts it. Web and Android show it.
 */
export function RotatingPromo({
  tone = 'sidebar',
  className,
}: {
  /** `sidebar`: muted text on the dark sidebar. `page`: the light/dark app theme. */
  tone?: 'sidebar' | 'page';
  className?: string;
}) {
  const [allowed, setAllowed] = useState(false);
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    setAllowed(!isIosNative());
  }, []);

  useEffect(() => {
    if (!allowed) {
      return;
    }

    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIndex((i) => (i + 1) % PROMOS.length);
        setVisible(true);
      }, 300); // matches the CSS transition duration
    }, ROTATE_MS);

    return () => clearInterval(interval);
  }, [allowed]);

  if (!allowed) {
    return null;
  }

  const promo = PROMOS[index];

  return (
    <a
      href={promo.href}
      target="_blank"
      rel="noreferrer sponsored noopener"
      style={{
        opacity: visible ? 1 : 0,
        transition: 'opacity 300ms ease',
      }}
      className={twMerge(
        'flex items-center gap-x-2 rounded-sm px-3 py-1.5 text-xs no-underline transition-colors',
        tone === 'sidebar'
          ? 'text-zinc-400 hover:text-zinc-200'
          : 'text-zinc-500 active:text-zinc-700 dark:text-zinc-400 dark:active:text-zinc-200',
        className,
      )}
    >
      <GiftIcon className="size-4 shrink-0" aria-hidden="true" />
      <span className="truncate">{promo.text}</span>
    </a>
  );
}

export default RotatingPromo;
